import React from 'react';
import { Footprints, Bike, Flame, UtensilsCrossed } from 'lucide-react';
import { experiences, homestayInfo } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const Experiences = () => {
  const iconMap = {
    Footprints,
    Bike,
    Flame,
    UtensilsCrossed
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello! I'd like to know more about the experiences at White Orchid Homestay.");
    window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Unique Experiences</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Complimentary nature adventures and memorable moments await you
          </p>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-96 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1742230861989-c4c32be0626f"
          alt="Pine Forest Dowhill"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60 flex items-center justify-center">
          <div className="text-center text-white">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Explore Dowhill Pine Forest</h2>
            <p className="text-lg md:text-xl text-white/90">Right at our doorstep</p>
          </div>
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="py-16 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="space-y-12 max-w-6xl mx-auto">
            {experiences.map((exp, index) => {
              const Icon = iconMap[exp.icon];
              return (
                <Card key={exp.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${
                    index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                  }`}>
                    {/* Image */}
                    <div className={`relative h-80 lg:h-auto ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                      {exp.showMultiple && exp.images.length > 1 ? (
                        <div className="grid grid-cols-2 gap-2 h-full p-2">
                          {exp.images.map((img, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={img}
                              alt={`${exp.title} ${imgIndex + 1}`}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ))}
                        </div>
                      ) : (
                        <img
                          src={exp.images[0]}
                          alt={exp.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {exp.complimentary && (
                        <div className="absolute top-4 left-4 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2">
                          <span>Complimentary for Guests</span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className={`p-8 lg:p-12 flex flex-col justify-center ${
                      index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''
                    }`}>
                      <div className="space-y-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#1F4D3A] to-[#A3B18A] rounded-lg flex items-center justify-center">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-3xl font-bold text-[#1F4D3A] mb-3">{exp.title}</h3>
                          {exp.complimentary && (
                            <Badge className="bg-[#25D366] hover:bg-[#20BA5A] mb-4">
                              Free for All Guests
                            </Badge>
                          )}
                        </div>
                        <p className="text-lg text-gray-700 leading-relaxed">{exp.description}</p>
                        {!exp.complimentary && (
                          <p className="text-sm text-gray-600 italic">Available on request</p>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What Makes Our Experiences Special */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#1F4D3A] mb-4">
                What Makes Our Experiences Special
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-[#A3B18A]/20">
                <CardContent className="p-6 space-y-3">
                  <div className="w-12 h-12 bg-[#1F4D3A]/10 rounded-lg flex items-center justify-center mb-2">
                    <Footprints className="w-6 h-6 text-[#1F4D3A]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1F4D3A]">Guided by Experts</h3>
                  <p className="text-gray-600">Our experienced local guides know the forest trails and hidden gems of Kurseong intimately.</p>
                </CardContent>
              </Card>
              <Card className="border-[#A3B18A]/20">
                <CardContent className="p-6 space-y-3">
                  <div className="w-12 h-12 bg-[#1F4D3A]/10 rounded-lg flex items-center justify-center mb-2">
                    <Bike className="w-6 h-6 text-[#1F4D3A]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1F4D3A]">Equipment Provided</h3>
                  <p className="text-gray-600">All necessary equipment for cycling tours is provided. Just bring your enthusiasm!</p>
                </CardContent>
              </Card>
              <Card className="border-[#A3B18A]/20">
                <CardContent className="p-6 space-y-3">
                  <div className="w-12 h-12 bg-[#1F4D3A]/10 rounded-lg flex items-center justify-center mb-2">
                    <Flame className="w-6 h-6 text-[#1F4D3A]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1F4D3A]">Perfect for Families</h3>
                  <p className="text-gray-600">All our experiences are family-friendly and suitable for different age groups.</p>
                </CardContent>
              </Card>
              <Card className="border-[#A3B18A]/20">
                <CardContent className="p-6 space-y-3">
                  <div className="w-12 h-12 bg-[#1F4D3A]/10 rounded-lg flex items-center justify-center mb-2">
                    <UtensilsCrossed className="w-6 h-6 text-[#1F4D3A]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1F4D3A]">Safe & Secure</h3>
                  <p className="text-gray-600">Your safety is our priority. All activities are conducted with proper safety measures.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready for an Adventure?</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Book your stay and enjoy complimentary cycling tours and nature hikes
          </p>
          <Button
            size="lg"
            onClick={handleWhatsAppClick}
            className="bg-[#25D366] hover:bg-[#20BA5A] text-white text-lg px-8 py-6"
          >
            Plan Your Experience
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Experiences;
